import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { FormsModule } from '@angular/forms';
import { NzUploadModule, NzUploadFile } from 'ng-zorro-antd/upload';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EmailCampaignService } from '../services/email-campaign.service';

@Component({
  selector: 'app-admin-email-campaign-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NzCardModule,
    NzButtonModule,
    NzInputModule,
    NzSelectModule,
    NzTableModule,
    NzTagModule,
    NzUploadModule,
  ],
  templateUrl: './admin-email-campaign-editor.component.html',
  styleUrl: './admin-email-campaign-editor.component.less',
})
export class AdminEmailCampaignEditorComponent implements OnInit {
  form: FormGroup;
  campaignId: string | null = null;
  loading = false;
  saving = false;
  templates: any[] = [];
  blockTypes = ['hero', 'text', 'image', 'feature_list', 'cta', 'divider', 'footer_note'];
  previewHtml: SafeHtml = '';

  testEmail = '';
  recipientMode: 'single' | 'paste' | 'users' = 'single';
  singleEmail = '';
  pastedEmails = '';
  userSearch = '';
  users: Array<{ id: string; email: string; displayName: string }> = [];
  selectedUserIds: string[] = [];

  sends: any[] = [];
  latestSendStatus = '';
  selectedSendId = '';
  recipients: any[] = [];
  recipientsLoading = false;

  private templateAutoApplyEnabled = true;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private service: EmailCampaignService,
    private message: NzMessageService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      templateId: [null],
      subject: ['', [Validators.required]],
      preheader: [''],
      senderName: ['Scanafy'],
      senderEmail: [''],
      replyTo: [''],
      addBlockType: ['text'],
      blocks: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.paramMap.get('id');
    this.loadTemplates();

    this.form.get('templateId')?.valueChanges.subscribe((templateId) => {
      if (!this.templateAutoApplyEnabled) {
        return;
      }
      this.applyTemplate(templateId);
    });

    if (this.campaignId) {
      this.loadCampaign(this.campaignId);
      this.loadSends(this.campaignId);
    } else {
      this.addBlock('hero');
      this.form.markAsPristine();
    }

    this.searchUsers();
  }

  get blocks(): FormArray {
    return this.form.get('blocks') as FormArray;
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  createBlock(type: string, content?: any): FormGroup {
    return this.fb.group({
      type: [type, Validators.required],
      content: [this.normalizeContent(content)],
    });
  }

  normalizeContent(content: any): Record<string, any> {
    return (content && typeof content === 'object') ? { ...content } : {};
  }

  normalizeBlocks(blocks: any[]): Array<{ type: string; content: Record<string, any> }> {
    if (!Array.isArray(blocks)) {
      return [];
    }

    return blocks
      .map((block) => ({
        type: String(block?.type || '').trim(),
        content: this.normalizeContent(block?.content),
      }))
      .filter((block) => !!block.type);
  }

  blockContent(index: number): Record<string, any> {
    return this.normalizeContent(this.blocks.at(index)?.get('content')?.value);
  }

  addBlock(type = 'text'): void {
    this.blocks.push(this.createBlock(type, {}));
    this.form.markAsDirty();
  }

  addBlockFromToolbar(): void {
    const selected = String(this.form.get('addBlockType')?.value || 'text');
    this.addBlock(selected);
  }

  removeBlock(index: number): void {
    this.blocks.removeAt(index);
    this.form.markAsDirty();
  }

  moveBlockUp(index: number): void {
    if (index <= 0) {
      return;
    }
    const control = this.blocks.at(index);
    this.blocks.removeAt(index);
    this.blocks.insert(index - 1, control);
    this.form.markAsDirty();
  }

  moveBlockDown(index: number): void {
    if (index >= this.blocks.length - 1) {
      return;
    }
    const control = this.blocks.at(index);
    this.blocks.removeAt(index);
    this.blocks.insert(index + 1, control);
    this.form.markAsDirty();
  }

  loadTemplates(): void {
    this.service.listTemplates().subscribe({
      next: (rows) => {
        this.templates = rows || [];
      },
      error: () => {
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_LOAD_TEMPLATES_FAILED'));
      },
    });
  }

  applyTemplate(templateId: string): void {
    if (!templateId) {
      return;
    }

    const template = this.templates.find((item) => item._id === templateId);
    if (!template) {
      return;
    }

    this.form.patchValue({
      name: this.form.value.name || template.name,
      subject: template.defaultSubject || this.form.value.subject,
      preheader: template.defaultPreheader || this.form.value.preheader,
    }, { emitEvent: false });

    this.blocks.clear();
    this.normalizeBlocks(template.defaultBlocks || []).forEach((block) => {
      this.blocks.push(this.createBlock(block.type, block.content));
    });
    this.form.markAsDirty();
  }

  loadCampaign(id: string): void {
    this.loading = true;
    this.service.getCampaign(id).subscribe({
      next: (campaign) => {
        this.loading = false;

        this.templateAutoApplyEnabled = false;
        this.form.patchValue({
          name: campaign.name,
          templateId: campaign.templateId,
          subject: campaign.subject,
          preheader: campaign.preheader,
          senderName: campaign.senderName,
          senderEmail: campaign.senderEmail,
          replyTo: campaign.replyTo,
        }, { emitEvent: false });

        this.blocks.clear();
        this.normalizeBlocks(campaign.blocks || []).forEach((block) => {
          this.blocks.push(this.createBlock(block.type, block.content));
        });

        this.templateAutoApplyEnabled = true;
        this.form.markAsPristine();
      },
      error: () => {
        this.loading = false;
        this.templateAutoApplyEnabled = true;
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_LOAD_CAMPAIGN_FAILED'));
      },
    });
  }

  buildPayload(status?: string): any {
    const raw = this.form.getRawValue();
    return {
      name: raw.name,
      templateId: raw.templateId,
      subject: raw.subject,
      preheader: raw.preheader,
      senderName: raw.senderName,
      senderEmail: raw.senderEmail,
      replyTo: raw.replyTo,
      blocks: this.normalizeBlocks(raw.blocks || []),
      ...(status ? { status } : {}),
    };
  }

  persistCurrentCampaign(onSuccess: () => void): void {
    if (!this.campaignId) {
      this.message.warning(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_SAVE_FIRST'));
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message.warning(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_FORM_INVALID'));
      return;
    }

    if (!this.form.dirty) {
      onSuccess();
      return;
    }

    this.saving = true;
    this.service.updateCampaign(this.campaignId, this.buildPayload()).subscribe({
      next: () => {
        this.saving = false;
        this.form.markAsPristine();
        onSuccess();
      },
      error: () => {
        this.saving = false;
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_SYNC_FAILED'));
      },
    });
  }

  saveDraft(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message.warning(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_FORM_INVALID'));
      return;
    }

    this.saving = true;
    const payload = this.buildPayload('draft');

    const req = this.campaignId
      ? this.service.updateCampaign(this.campaignId, payload)
      : this.service.createCampaign(payload);

    req.subscribe({
      next: (res: any) => {
        this.saving = false;
        this.campaignId = res?._id || this.campaignId;
        this.form.markAsPristine();
        this.message.success(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_DRAFT_SAVED'));

        if (this.campaignId) {
          this.router.navigateByUrl(`/admin/email-campaigns/${this.campaignId}`);
          this.loadSends(this.campaignId);
        }
      },
      error: () => {
        this.saving = false;
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_SAVE_FAILED'));
      },
    });
  }

  preview(): void {
    this.persistCurrentCampaign(() => {
      this.service.previewCampaign(this.campaignId as string).subscribe({
        next: (res) => {
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(res.html || '');
        },
        error: () => {
          this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_PREVIEW_FAILED'));
        },
      });
    });
  }

  sendTest(): void {
    if (!this.testEmail) {
      this.message.warning(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_TEST_EMAIL_REQUIRED'));
      return;
    }

    this.persistCurrentCampaign(() => {
      this.service.sendTestEmail(this.campaignId as string, this.testEmail).subscribe({
        next: () => {
          this.message.success(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_TEST_SENT'));
          this.loadSends(this.campaignId as string);
        },
        error: () => this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_TEST_FAILED')),
      });
    });
  }

  sendNow(): void {
    const payload: any = {};
    if (this.recipientMode === 'single') {
      payload.singleEmail = this.singleEmail;
    } else if (this.recipientMode === 'paste') {
      payload.pastedEmails = this.pastedEmails;
    } else {
      payload.userIds = this.selectedUserIds;
    }

    this.persistCurrentCampaign(() => {
      payload.idempotencyKey = `${this.campaignId}-${Date.now()}`;

      this.service.sendCampaign(this.campaignId as string, payload).subscribe({
        next: () => {
          this.message.success(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_QUEUED'));
          this.loadSends(this.campaignId as string);
        },
        error: () => this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_QUEUE_FAILED')),
      });
    });
  }

  searchUsers(): void {
    this.service.searchUsers(this.userSearch, 1, 50).subscribe({
      next: (res: any) => {
        this.users = (res?.items || []).map((item: any) => ({
          id: item.id,
          email: item.email,
          displayName: item.displayName,
        }));
      },
      error: () => {
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_USERS_LOAD_FAILED'));
      },
    });
  }

  linesToItems(value: string): string[] {
    return String(value || '')
      .split('\n')
      .map((entry) => entry.trim())
      .filter((entry) => !!entry);
  }

  updateBlockContent(index: number, key: string, value: any): void {
    const next = { ...this.blockContent(index), [key]: value };
    this.blocks.at(index).patchValue({ content: next });
    this.form.markAsDirty();
  }

  uploadImageForBlock(index: number, file: NzUploadFile): boolean {
    if (!this.campaignId) {
      this.message.warning(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_SAVE_BEFORE_UPLOAD'));
      return false;
    }

    const native = file as any as File;
    this.service.uploadAsset(native, this.campaignId).subscribe({
      next: (asset: any) => {
        this.updateBlockContent(index, 'url', asset.url);
        if (!this.blockContent(index)['alt']) {
          this.updateBlockContent(index, 'alt', asset.originalName || 'image');
        }
        this.message.success(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_ASSET_UPLOADED'));
      },
      error: () => this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_ASSET_UPLOAD_FAILED')),
    });

    return false;
  }

  onSendSelected(sendId: string): void {
    this.selectedSendId = sendId;
    if (!this.campaignId || !sendId) {
      this.recipients = [];
      return;
    }
    this.loadRecipients(this.campaignId, sendId);
  }


  syncLatestSendStatus(): void {
    this.latestSendStatus = String(this.sends?.[0]?.status || '').toLowerCase();
  }

  isLatestSendQueuedOrProcessing(): boolean {
    return this.latestSendStatus === 'queued' || this.latestSendStatus === 'processing';
  }
  loadSends(campaignId: string): void {
    this.service.listSends(campaignId).subscribe({
      next: (rows) => {
        this.sends = rows || [];
        this.syncLatestSendStatus();
      },
      error: () => {
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_LOAD_SEND_HISTORY_FAILED'));
      },
    });
  }

  loadRecipients(campaignId: string, sendId: string): void {
    this.recipientsLoading = true;
    this.service.listRecipients(campaignId, sendId, 1, 50).subscribe({
      next: (res: any) => {
        this.recipientsLoading = false;
        this.recipients = res?.items || [];
      },
      error: () => {
        this.recipientsLoading = false;
        this.message.error(this.t('ADMIN.EMAIL_CAMPAIGNS.MSG_LOAD_RECIPIENTS_FAILED'));
      },
    });
  }

  blockTypeLabel(type: string): string {
    return this.t(`ADMIN.EMAIL_CAMPAIGNS.BLOCK_TYPES.${String(type || '').toUpperCase()}`);
  }
}



